import { useState, useEffect, useCallback, startTransition } from 'react';
import { isDemoBrand } from '../utils/constants';
import { fetchWithBrand } from '../utils/api';

/**
 * Fetches /api/brands/{id}/notifications, provides markRead, markAllRead,
 * deleteNotification, and the daily/weekly/monthly comparison notification generators.
 */
export function useNotifications(brandId, dateRange) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  // Fetch notifications
  const fetchNotifications = useCallback(async (unreadOnly = false) => {
    if (!brandId) return;

    // Demo notifications
    if (isDemoBrand(brandId)) {
      const demo = [
        { id: 'n1', title: 'Demo: Campaign budget optimized', message: 'PMax budget adjusted +15% for high ROAS.', type: 'info', priority: 'normal', is_read: false, createdAt: new Date().toISOString() },
        { id: 'n2', title: 'Demo: Low inventory alert', message: 'Top category stock decreasing, check Merchant Center.', type: 'warning', priority: 'high', is_read: false, createdAt: new Date().toISOString() },
      ];
      setNotifications(demo);
      setUnreadCount(demo.filter(n => !n.is_read).length);
      return;
    }

    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        limit: '20',
        offset: '0',
        unreadOnly: unreadOnly.toString(),
      });

      const response = await fetchWithBrand(`/api/brands/${brandId}/notifications?${params}`, brandId);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setNotifications(data.notifications);
          setUnreadCount(data.unreadCount);
        }
      }
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  }, [brandId]);

  // Refetch on brandId or dateRange change
  useEffect(() => {
    fetchNotifications();
  }, [brandId, dateRange, fetchNotifications]);

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId) => {
    if (!brandId) return;
    try {
      const response = await fetchWithBrand(`/api/brands/${brandId}/notifications/${notificationId}/read`, brandId, { method: 'POST' });
      if (response.ok) {
        startTransition(() => {
          setNotifications(prev =>
            prev.map(notif =>
              notif.id === notificationId
                ? { ...notif, is_read: true, read_at: new Date().toISOString() }
                : notif
            )
          );
        });
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch {
      // silent
    }
  }, [brandId]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    if (!brandId) return;
    try {
      const response = await fetchWithBrand(`/api/brands/${brandId}/notifications/mark-all-read`, brandId, { method: 'POST' });
      if (response.ok) {
        startTransition(() => {
          setNotifications(prev =>
            prev.map(notif => ({ ...notif, is_read: true, read_at: new Date().toISOString() }))
          );
          setUnreadCount(0);
        });
      }
    } catch {
      // silent
    }
  }, [brandId]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    if (!brandId) return;
    try {
      const response = await fetchWithBrand(`/api/brands/${brandId}/notifications/${notificationId}`, brandId, { method: 'DELETE' });
      if (response.ok) {
        const deletedNotification = notifications.find(notif => notif.id === notificationId);
        startTransition(() => {
          setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
        });
        if (deletedNotification && !deletedNotification.is_read) {
          startTransition(() => {
            setUnreadCount(prev => Math.max(0, prev - 1));
          });
        }
      }
    } catch {
      // silent
    }
  }, [brandId, notifications]);

  // Toggle notification modal
  const toggleModal = useCallback(() => {
    setIsNotificationOpen(prev => !prev);
  }, []);

  // --- Comparison Notification Generators ---

  // DAILY COMPARISON NOTIFICATION
  const createDailyComparison = useCallback(async () => {
    if (!brandId) return;
    try {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const dayBeforeYesterday = new Date(today);
      dayBeforeYesterday.setDate(today.getDate() - 2);

      const yesterdayStr = yesterday.toISOString().split('T')[0];
      const dayBeforeYesterdayStr = dayBeforeYesterday.toISOString().split('T')[0];

      const fetchUnifiedData = async (endpoint, sDate, eDate) => {
        try {
          const response = await fetchWithBrand(
            `/api/google-ads/${endpoint}?dateRange=custom&startDate=${sDate}&endDate=${eDate}`,
            brandId
          );
          if (response.ok) {
            const data = await response.json();
            return Array.isArray(data) ? data : [];
          }
          return [];
        } catch {
          return [];
        }
      };

      const fetchGa4DailyTotals = async (dateStr) => {
        try {
          const url = `/api/google-ads/ga-traffic-source?dateRange=custom&startDate=${dateStr}&endDate=${dateStr}`;
          const response = await fetchWithBrand(url, brandId);
          if (!response.ok) return { sessions: 0, revenue: 0 };
          const data = await response.json();
          const rows = Array.isArray(data) ? data : Array.isArray(data?.detailedData) ? data.detailedData : [];
          return rows.reduce((acc, row) => {
            acc.sessions += Number(row.sessions ?? row.ga4_sessions ?? 0) || 0;
            acc.revenue += Number(row.totalRevenue ?? row.ga4_total_revenue ?? 0) || 0;
            return acc;
          }, { sessions: 0, revenue: 0 });
        } catch {
          return { sessions: 0, revenue: 0 };
        }
      };

      const [yesterdayGoogleData, dayBeforeGoogleData, yesterdayMetaData, dayBeforeMetaData, yesterdayGa4Totals, dayBeforeGa4Totals] = await Promise.all([
        fetchUnifiedData('unified-googleads-ga4-performance', yesterdayStr, yesterdayStr),
        fetchUnifiedData('unified-googleads-ga4-performance', dayBeforeYesterdayStr, dayBeforeYesterdayStr),
        fetchUnifiedData('unified-meta-ga4-performance', yesterdayStr, yesterdayStr),
        fetchUnifiedData('unified-meta-ga4-performance', dayBeforeYesterdayStr, dayBeforeYesterdayStr),
        fetchGa4DailyTotals(yesterdayStr),
        fetchGa4DailyTotals(dayBeforeYesterdayStr),
      ]);

      const calculateGoogleTotals = (data) => data.reduce((acc, day) => ({
        cost: acc.cost + (parseFloat(day.cost) || 0),
        revenue: acc.revenue + (parseFloat(day.conversions_value) || 0),
        clicks: acc.clicks + (parseInt(day.clicks) || 0),
        sessions: acc.sessions + (parseInt(day.ga4_sessions ?? day.sessions ?? 0) || 0),
        analyticsRevenue: acc.analyticsRevenue + (parseFloat(day.ga4_total_revenue ?? day.purchase_revenue ?? 0) || 0),
      }), { cost: 0, revenue: 0, clicks: 0, sessions: 0, analyticsRevenue: 0 });

      const calculateMetaTotals = (data) => data.reduce((acc, day) => ({
        cost: acc.cost + (parseFloat(day.spend) || 0),
        revenue: acc.revenue + (parseFloat(day.purchase_roas) * parseFloat(day.spend) || 0),
        clicks: acc.clicks + (parseInt(day.clicks) || 0),
      }), { cost: 0, revenue: 0, clicks: 0 });

      const yesterdayGoogle = calculateGoogleTotals(yesterdayGoogleData);
      const dayBeforeGoogle = calculateGoogleTotals(dayBeforeGoogleData);
      const yesterdayMeta = calculateMetaTotals(yesterdayMetaData);
      const dayBeforeMeta = calculateMetaTotals(dayBeforeMetaData);

      const calculateChange = (current, previous) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return ((current - previous) / previous) * 100;
      };
      const formatChange = (value) => value > 0 ? `+${value.toFixed(1)}%` : `${value.toFixed(1)}%`;
      const formatCurrency = (value) => Math.round(value).toLocaleString('en-US');
      const formatNumber = (value) => value.toLocaleString('en-US');

      const changes = {
        googleAds: { cost: yesterdayGoogle.cost, costChange: calculateChange(yesterdayGoogle.cost, dayBeforeGoogle.cost) },
        metaAds: { cost: yesterdayMeta.cost, costChange: calculateChange(yesterdayMeta.cost, dayBeforeMeta.cost) },
        analytics: {
          sessions: yesterdayGa4Totals.sessions,
          sessionsChange: calculateChange(yesterdayGa4Totals.sessions, dayBeforeGa4Totals.sessions),
          revenue: yesterdayGa4Totals.revenue,
          revenueChange: calculateChange(yesterdayGa4Totals.revenue, dayBeforeGa4Totals.revenue),
        },
        totals: {
          cost: yesterdayGoogle.cost + yesterdayMeta.cost,
          costChange: calculateChange(yesterdayGoogle.cost + yesterdayMeta.cost, dayBeforeGoogle.cost + dayBeforeMeta.cost),
        },
      };

      const yesterdayDisplay = yesterday.toLocaleDateString('en-US', { day: 'numeric', month: 'long' });
      let message = `Date: ${yesterdayDisplay}\n\nGoogle Ads: ${formatCurrency(changes.googleAds.cost)} (${formatChange(changes.googleAds.costChange)})\nMeta Ads: ${formatCurrency(changes.metaAds.cost)} (${formatChange(changes.metaAds.costChange)})\nGA4 Sessions: ${formatNumber(changes.analytics.sessions)} (${formatChange(changes.analytics.sessionsChange)})\nGA4 Revenue: ${formatCurrency(changes.analytics.revenue)} (${formatChange(changes.analytics.revenueChange)})\n\nTotal Spend: ${formatCurrency(changes.totals.cost)} (${formatChange(changes.totals.costChange)})`;

      const notification = {
        type: 'performance_alert',
        title: 'Daily Performance Summary',
        message,
        priority: 'normal',
        category: 'performance',
      };

      const response = await fetchWithBrand(`/api/brands/${brandId}/notifications`, brandId, {
        method: 'POST',
        body: JSON.stringify(notification),
      });

      if (response.ok) {
        setTimeout(() => fetchNotifications(), 500);
      }
    } catch {
      // silent
    }
  }, [brandId, fetchNotifications]);

  // WEEKLY COMPARISON NOTIFICATION
  const createWeeklyComparison = useCallback(async () => {
    if (!brandId) return;
    try {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const daysToLastMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const daysToLastCompletedMonday = daysToLastMonday + 7;

      const lastCompletedMonday = new Date(today);
      lastCompletedMonday.setDate(today.getDate() - daysToLastCompletedMonday);
      const mondayStr = lastCompletedMonday.toISOString().split('T')[0];

      const comparisonResponse = await fetchWithBrand(`/api/google-ads/weekly-comparison?date=${mondayStr}`, brandId);
      if (!comparisonResponse.ok) return;

      const comparisonData = await comparisonResponse.json();
      if (!comparisonData.success || !comparisonData.comparison) return;

      const comparison = comparisonData.comparison;
      const changes = comparison.changes;

      const formatChange = (value) => value > 0 ? `+${value.toFixed(1)}%` : `${value.toFixed(1)}%`;
      const formatCurrency = (value) => Math.round(value).toLocaleString('en-US');
      const formatNumber = (value) => value.toLocaleString('en-US');

      const currentWeekStart = new Date(comparison.currentWeek.start).toLocaleDateString('en-US', { day: 'numeric', month: 'long' });
      const currentWeekEnd = new Date(comparison.currentWeek.end).toLocaleDateString('en-US', { day: 'numeric', month: 'long' });

      let message = `Week: ${currentWeekStart} - ${currentWeekEnd}\n\nGoogle Ads: ${formatCurrency(changes.googleAds.cost)} (${formatChange(changes.googleAds.costChange)})\nMeta Ads: ${formatCurrency(changes.metaAds.cost)} (${formatChange(changes.metaAds.costChange)})\nGA4 Sessions: ${formatNumber(changes.analytics.sessions)} (${formatChange(changes.analytics.sessionsChange)})\nGA4 Revenue: ${formatCurrency(changes.analytics.revenue)} (${formatChange(changes.analytics.revenueChange)})\n\nTotal Spend: ${formatCurrency(changes.totals.cost)} (${formatChange(changes.totals.costChange)})`;

      const notification = {
        type: 'performance_alert',
        title: 'Weekly Performance Summary',
        message,
        priority: 'normal',
        category: 'performance',
      };

      const response = await fetchWithBrand(`/api/brands/${brandId}/notifications`, brandId, {
        method: 'POST',
        body: JSON.stringify(notification),
      });

      if (response.ok) {
        setTimeout(() => fetchNotifications(), 500);
      }
    } catch {
      // silent
    }
  }, [brandId, fetchNotifications]);

  // MONTHLY COMPARISON NOTIFICATION
  const createMonthlyComparison = useCallback(async () => {
    if (!brandId) return;
    try {
      const today = new Date();
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      const monthBeforeLast = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      const monthBeforeLastEnd = new Date(today.getFullYear(), today.getMonth() - 1, 0);

      const lastMonthStartStr = lastMonth.toISOString().split('T')[0];
      const lastMonthEndStr = lastMonthEnd.toISOString().split('T')[0];
      const monthBeforeLastStartStr = monthBeforeLast.toISOString().split('T')[0];
      const monthBeforeLastEndStr = monthBeforeLastEnd.toISOString().split('T')[0];

      const fetchUnifiedData = async (endpoint, sDate, eDate) => {
        try {
          const response = await fetchWithBrand(
            `/api/google-ads/${endpoint}?dateRange=custom&startDate=${sDate}&endDate=${eDate}`,
            brandId
          );
          if (response.ok) {
            const data = await response.json();
            return Array.isArray(data) ? data : [];
          }
          return [];
        } catch {
          return [];
        }
      };

      const [lastMonthGoogleData, monthBeforeGoogleData, lastMonthMetaData, monthBeforeMetaData] = await Promise.all([
        fetchUnifiedData('unified-googleads-ga4-performance', lastMonthStartStr, lastMonthEndStr),
        fetchUnifiedData('unified-googleads-ga4-performance', monthBeforeLastStartStr, monthBeforeLastEndStr),
        fetchUnifiedData('unified-meta-ga4-performance', lastMonthStartStr, lastMonthEndStr),
        fetchUnifiedData('unified-meta-ga4-performance', monthBeforeLastStartStr, monthBeforeLastEndStr),
      ]);

      const calculateGoogleTotals = (data) => data.reduce((acc, day) => ({
        cost: acc.cost + (parseFloat(day.cost) || 0),
        revenue: acc.revenue + (parseFloat(day.conversions_value) || 0),
        clicks: acc.clicks + (parseInt(day.clicks) || 0),
        sessions: acc.sessions + (parseInt(day.sessions ?? day.ga4_sessions ?? 0) || 0),
        analyticsRevenue: acc.analyticsRevenue + (parseFloat(day.purchase_revenue ?? day.ga4_total_revenue ?? 0) || 0),
      }), { cost: 0, revenue: 0, clicks: 0, sessions: 0, analyticsRevenue: 0 });

      const calculateMetaTotals = (data) => data.reduce((acc, day) => ({
        cost: acc.cost + (parseFloat(day.spend) || 0),
        revenue: acc.revenue + (parseFloat(day.purchase_roas) * parseFloat(day.spend) || 0),
        clicks: acc.clicks + (parseInt(day.clicks) || 0),
      }), { cost: 0, revenue: 0, clicks: 0 });

      const lastMonthGoogle = calculateGoogleTotals(lastMonthGoogleData);
      const monthBeforeGoogle = calculateGoogleTotals(monthBeforeGoogleData);
      const lastMonthMeta = calculateMetaTotals(lastMonthMetaData);
      const monthBeforeMeta = calculateMetaTotals(monthBeforeMetaData);

      const calculateChange = (current, previous) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return ((current - previous) / previous) * 100;
      };
      const formatChange = (value) => value > 0 ? `+${value.toFixed(1)}%` : `${value.toFixed(1)}%`;
      const formatCurrency = (value) => Math.round(value).toLocaleString('en-US');
      const formatNumber = (value) => value.toLocaleString('en-US');

      const changes = {
        googleAds: { cost: lastMonthGoogle.cost, costChange: calculateChange(lastMonthGoogle.cost, monthBeforeGoogle.cost) },
        metaAds: { cost: lastMonthMeta.cost, costChange: calculateChange(lastMonthMeta.cost, monthBeforeMeta.cost) },
        analytics: {
          sessions: lastMonthGoogle.sessions,
          sessionsChange: calculateChange(lastMonthGoogle.sessions, monthBeforeGoogle.sessions),
          revenue: lastMonthGoogle.analyticsRevenue,
          revenueChange: calculateChange(lastMonthGoogle.analyticsRevenue, monthBeforeGoogle.analyticsRevenue),
        },
        totals: {
          cost: lastMonthGoogle.cost + lastMonthMeta.cost,
          costChange: calculateChange(lastMonthGoogle.cost + lastMonthMeta.cost, monthBeforeGoogle.cost + monthBeforeMeta.cost),
        },
      };

      const lastMonthDisplay = lastMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      let message = `Month: ${lastMonthDisplay}\n\nGoogle Ads: ${formatCurrency(changes.googleAds.cost)} (${formatChange(changes.googleAds.costChange)})\nMeta Ads: ${formatCurrency(changes.metaAds.cost)} (${formatChange(changes.metaAds.costChange)})\nGA4 Sessions: ${formatNumber(changes.analytics.sessions)} (${formatChange(changes.analytics.sessionsChange)})\nGA4 Revenue: ${formatCurrency(changes.analytics.revenue)} (${formatChange(changes.analytics.revenueChange)})\n\nTotal Spend: ${formatCurrency(changes.totals.cost)} (${formatChange(changes.totals.costChange)})`;

      const notification = {
        type: 'performance_alert',
        title: 'Monthly Performance Summary',
        message,
        priority: 'normal',
        category: 'performance',
      };

      const response = await fetchWithBrand(`/api/brands/${brandId}/notifications`, brandId, {
        method: 'POST',
        body: JSON.stringify(notification),
      });

      if (response.ok) {
        setTimeout(() => fetchNotifications(), 500);
      }
    } catch {
      // silent
    }
  }, [brandId, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    isNotificationOpen,
    setIsNotificationOpen,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    toggleModal,
    refetch: fetchNotifications,
    createDailyComparison,
    createWeeklyComparison,
    createMonthlyComparison,
  };
}
